import React, { useRef, useState, useEffect } from 'react';
import './input.css';

/**
 * One box per character of a short code, submitted as soon as the last box fills.
 *
 * `length` defaults to 6 — the TOTP codes LoginPage and Dialog2FAField ask for.
 * Telegram's login codes are usually 5 digits and the API says how many, so the
 * session login passes its own.
 */
export default function CodeAuthField({ callback, reset, isLoading, length = 6 }) {
    const [code, setCode] = useState('');

    // One ref holding every box rather than a useRef per box: the box count is a
    // prop, and hooks cannot be called a variable number of times.
    const inputRefs = useRef([]);
    const boxes = Array.from({ length }, (_, index) => index);

    // Reset all inputs and clear state
    const resetCode = () => {
        inputRefs.current.slice(0, length).forEach((input) => {
            if (input) input.value = '';
        });
        inputRefs.current[0]?.focus();
        setCode('');
    };

    // Call our callback once every box is filled
    useEffect(() => {
        if (code.length === length) {
            if (typeof callback === 'function') callback(code);
            resetCode();
        }
    }, [code]); //eslint-disable-line

    // Listen for external reset toggle
    useEffect(() => {
        resetCode();
    }, [reset]); //eslint-disable-line

    // Handle input
    function handleInput(e, index) {
        const input = e.target;
        const previousInput = inputRefs.current[index - 1];
        const nextInput = inputRefs.current[index + 1];

        // Update code state with single digit
        const newCode = [...code];
        // Convert lowercase letters to uppercase
        if (/^[a-z]+$/.test(input.value)) {
            const uc = input.value.toUpperCase();
            newCode[index] = uc;
            inputRefs.current[index].value = uc;
        } else {
            newCode[index] = input.value;
        }
        setCode(newCode.join(''));

        input.select();

        if (input.value === '') {
            // If the value is deleted, select previous input, if exists
            if (previousInput) {
                previousInput.focus();
            }
        } else if (nextInput) {
            // Select next input on entry, if exists
            nextInput.select();
        }
    }

    // Select the contents on focus
    function handleFocus(e) {
        e.target.select();
    }

    // Handle backspace key
    function handleKeyDown(e, index) {
        const input = e.target;
        const previousInput = inputRefs.current[index - 1];

        if ((e.keyCode === 8 || e.keyCode === 46) && input.value === '') {
            e.preventDefault();
            setCode(
                (prevCode) =>
                    prevCode.slice(0, index) + prevCode.slice(index + 1)
            );
            if (previousInput) {
                previousInput.focus();
            }
        }
    }

    // Capture pasted characters
    const handlePaste = (e) => {
        const pastedCode = e.clipboardData.getData('text');
        if (pastedCode.length === length) {
            setCode(pastedCode);
            inputRefs.current.slice(0, length).forEach((input, index) => {
                if (input) input.value = pastedCode.charAt(index);
            });
        }
    };

    return (
        <div className="flex gap-2 relative">
            {boxes.map((index) => (
                <input
                    className="custom-input"
                    key={index}
                    type="text"
                    maxLength={1}
                    onChange={(e) => handleInput(e, index)}
                    ref={(el) => {
                        inputRefs.current[index] = el;
                    }}
                    autoFocus={index === 0}
                    onFocus={handleFocus}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    disabled={isLoading}
                />
            ))}
        </div>
    );
}
